// export const rgbaToHex = (color) => {
//   // Handle string format (rgba or rgb)
//   if (typeof color === "string") {
//     const rgbaMatch = color.match(
//       /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+))?\)/
//     );
//     if (rgbaMatch) {
//       const [, r, g, b] = rgbaMatch;
//       return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
//     }
//   }

//   // Handle object format
//   if (typeof color === "object") {
//     const { r, g, b } = color;
//     return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
//   }

//   return "#000000"; // Default fallback
// };

export const getPrimaryColor = (data) => {
  if (!data || typeof data !== "object") {
    return "rgb(0, 0, 0)";
  }

  const { r = 0, g = 0, b = 0 } = data;
  return `rgb(${r}, ${g}, ${b})`;
};

export const rgbStringToObject = (rgbString) => {
  // Validate input
  if (!rgbString || typeof rgbString !== "string") {
    throw new Error("Invalid RGB string: input must be a string");
  }

  // Extract numbers from rgb string using regex
  const match = rgbString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);

  if (!match) {
    throw new Error("Invalid RGB string format. Expected format: rgb(r, g, b)");
  }

  return {
    r: parseInt(match[1], 10),
    g: parseInt(match[2], 10),
    b: parseInt(match[3], 10),
  };
};

export const rgbaToHex = (r, g, b, a = 1) => {
  // Validate inputs and provide defaults
  r = Number.isFinite(r) ? r : 0;
  g = Number.isFinite(g) ? g : 0;
  b = Number.isFinite(b) ? b : 0;
  a = Number.isFinite(a) ? a : 1;

  // Clamp values to valid ranges
  r = Math.max(0, Math.min(255, Math.round(r)));
  g = Math.max(0, Math.min(255, Math.round(g)));
  b = Math.max(0, Math.min(255, Math.round(b)));
  a = Math.max(0, Math.min(1, a));

  // Convert to hex
  const toHex = (n) => n.toString(16).padStart(2, "0");

  // Return with or without alpha
  if (a === 1) {
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  } else {
    const alphaHex = toHex(Math.round(a * 255));
    return `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`;
  }
};

export const darkenRGBColor = (color, darkenAmount) => {
  if (!color || typeof color !== "object") {
    return "rgb(0, 0, 0)";
  }

  const { r = 0, g = 0, b = 0 } = color;
  const amount = Math.min(Math.max(darkenAmount || 0, 0), 1);

  const newR = Math.max(0, Math.floor(r * (1 - amount)));
  const newG = Math.max(0, Math.floor(g * (1 - amount)));
  const newB = Math.max(0, Math.floor(b * (1 - amount)));

  return `rgb(${newR}, ${newG}, ${newB})`;
};

export const lightenRGBColor = (color, lightenAmount) => {
  if (!color || typeof color !== "object") {
    return "rgb(255, 255, 255)";
  }

  const { r = 0, g = 0, b = 0 } = color;
  const amount = Math.min(Math.max(lightenAmount || 0, 0), 1);

  const newR = Math.min(
    255,
    Math.floor(parseInt(r, 10) + (255 - parseInt(r, 10)) * amount)
  );
  const newG = Math.min(
    255,
    Math.floor(parseInt(g, 10) + (255 - parseInt(g, 10)) * amount)
  );
  const newB = Math.min(
    255,
    Math.floor(parseInt(b, 10) + (255 - parseInt(b, 10)) * amount)
  );

  return `rgb(${newR}, ${newG}, ${newB})`;
};

export const brightenRGBColor = (color, brightenAmount) => {
  if (!color || typeof color !== "object") {
    return "rgb(255, 255, 255)";
  }

  const { r = 0, g = 0, b = 0 } = color;
  const amount = Math.min(Math.max(brightenAmount || 0, 0), 1);

  const newR = Math.min(255, Math.floor(parseInt(r, 10) * (1 + amount)));
  const newG = Math.min(255, Math.floor(parseInt(g, 10) * (1 + amount)));
  const newB = Math.min(255, Math.floor(parseInt(b, 10) * (1 + amount)));

  return `rgb(${newR}, ${newG}, ${newB})`;
};

const getRelativeLuminance = (r, g, b) => {
  // Convert to linear RGB first (gamma correction)
  const toLinear = (c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

export const isLightColor = (color, threshold = 0.5) => {
  if (!color || typeof color !== "object") {
    return false; // Default to dark if color is invalid
  }

  const { r = 0, g = 0, b = 0 } = color;
  return getRelativeLuminance(r, g, b) > threshold;
};

export const isDarkColor = (color, threshold = 0.02) => {
  if (!color || typeof color !== "object") {
    return true; // Default to dark if color is invalid
  }

  const { r = 0, g = 0, b = 0 } = color;
  return getRelativeLuminance(r, g, b) <= threshold;
};

export const hexToRgbaObject = (hex) => {
  // Validate input
  if (!hex || typeof hex !== "string") {
    throw new Error("Invalid hex color: input must be a string");
  }

  // Remove # if present
  hex = hex.replace(/^#/, "");

  // Validate hex characters
  if (!/^[0-9A-Fa-f]+$/.test(hex)) {
    throw new Error(
      "Invalid hex color format: contains non-hexadecimal characters"
    );
  }

  // Handle 3-character hex codes (e.g., #f0a -> #ff00aa)
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  // Handle 6-character hex codes
  if (hex.length === 6) {
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return { r, g, b, a: 1 };
  }

  // Handle 8-character hex codes (with alpha)
  if (hex.length === 8) {
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const a = parseInt(hex.substring(6, 8), 16) / 255; // Convert to 0-1 range
    return { r, g, b, a };
  }

  throw new Error(
    "Invalid hex color format. Expected #RGB, #RRGGBB, or #RRGGBBAA"
  );
};
